import type { OnSubmitFunction } from "@circle-react-shared/uikit/Form/Form";
import type { Editor } from "@tiptap/react";
import { useState } from "react";

import { t } from "@/i18n-js/instance";

import type { LinkAttrs } from "./types";
import { VALIDATION_SCHEMA } from "./validationSchema";

interface UseLinkPopoverFormParams {
  editor: Editor;
  isEmailEditor: boolean;
  onClose: () => void;
}

interface LinkFormValues {
  url: string;
}

export const useLinkPopoverForm = ({
  editor,
  isEmailEditor,
  onClose,
}: UseLinkPopoverFormParams) => {
  const linkAttrs = editor.getAttributes("link") as LinkAttrs;
  const previousURL = linkAttrs.href ?? "";
  const previousTarget = linkAttrs.target ?? null;
  const isEditingExistingLink = editor.isActive("link");

  const [shouldOpenInNewTab, setShouldOpenInNewTab] = useState<boolean>(
    previousTarget === "_blank",
  );

  const handleRemoveLink = (): void => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    onClose();
  };

  const handleSubmit: OnSubmitFunction<LinkFormValues> = async (
    { url },
    { setError },
  ) => {
    try {
      await VALIDATION_SCHEMA.validate({ url });

      const { from, to } = editor.state.selection;
      const hasSelection = from !== to;
      const selectedText = editor.state.doc.textBetween(from, to);
      const isOnlyWhitespace = hasSelection && selectedText.trim() === "";
      const target: string = shouldOpenInNewTab ? "_blank" : "_self";

      if (
        isEditingExistingLink ||
        (hasSelection && !isOnlyWhitespace) ||
        !isEmailEditor
      ) {
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .setLink({ href: url, target })
          .run();
      } else {
        editor
          .chain()
          .focus()
          .insertContent([
            {
              type: "text",
              text: url,
              marks: [{ type: "link", attrs: { href: url, target } }],
            },
          ])
          .run();
      }

      onClose();
    } catch (error: unknown) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : t("link_url_invalid");
      setError("url", { type: "manual", message });
    }
  };

  return {
    isEditingExistingLink,
    previousURL,
    shouldOpenInNewTab,
    setShouldOpenInNewTab,
    handleRemoveLink,
    handleSubmit,
  };
};
